import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orgSettingsApi } from '../features/orgSettings/api.ts';
import { dv } from '../i18n/dv.ts';
import { type Body } from '../lib/body.ts';
import { BodyToggle } from '../features/orgSettings/BodyToggle.tsx';

// Mirror of the backend formatter for the live preview.
function previewAgendaNumber(template: string, term: string, n: string): string {
  const now = new Date();
  const yyyy = String(now.getFullYear());
  let out = template
    .replaceAll('{TERM}', term || '')
    .replaceAll('{YYYY}', yyyy)
    .replaceAll('{YY}', yyyy.slice(-2));
  const num = Number(n) || 1;
  for (let pad = 6; pad >= 1; pad--) {
    out = out.replaceAll(`{${'#'.repeat(pad)}}`, String(num).padStart(pad, '0'));
  }
  return out;
}

// One configurable number format (template + next counter + live preview).
// Reused for agenda / yaumiyya / ninmun numbering.
function NumberFormatCard({
  label, placeholder, format, next, termNumber, onSave, saving, saved,
}: {
  label: string; placeholder: string; format: string; next: string; termNumber: string;
  onSave: (format: string, next: string) => void; saving: boolean; saved: boolean;
}): JSX.Element {
  const [fmt, setFmt] = useState(format);
  const [nxt, setNxt] = useState(next);
  return (
    <form
      className="space-y-3 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6"
      onSubmit={(e) => { e.preventDefault(); onSave(fmt, nxt); }}
    >
      <h3 className="status-label text-muted-foreground">{label}</h3>
      <input
        type="text"
        dir="ltr"
        value={fmt}
        maxLength={60}
        placeholder={placeholder}
        onChange={(e) => setFmt(e.target.value)}
        className="input font-mono"
      />
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm">{dv.settings.agendaNumberNext}</label>
        <input
          type="number"
          min={1}
          dir="ltr"
          value={nxt}
          onChange={(e) => setNxt(e.target.value)}
          className="input w-28"
        />
        <button
          type="submit"
          disabled={saving}
          className="btn-primary"
        >
          {dv.common.save}
        </button>
        {saved && <span className="text-sm text-[hsl(var(--success))]">{dv.settings.saved}</span>}
      </div>
      {fmt.trim() !== '' && (
        <p className="text-sm">
          {dv.settings.agendaNumberPreview}: <span dir="ltr" className="font-mono">{previewAgendaNumber(fmt, termNumber, nxt)}</span>
        </p>
      )}
    </form>
  );
}

export function NumberSettings(): JSX.Element {
  const qc = useQueryClient();
  // Council/WDC toggle. The selected body is the query key + the body sent to the
  // settings GET/PATCH, so the council tab shows the backfilled council values
  // while the WDC tab starts blank until configured.
  const [body, setBody] = useState<Body>('COUNCIL');
  const { data: org, isLoading } = useQuery({
    queryKey: ['org', body],
    queryFn: () => orgSettingsApi.get(body),
  });

  // Generic save for any of the three number configs; the payload keys are
  // built from the kind prefix (agenda/yaumiyya/ninmun). The body is threaded so
  // the write lands on the selected body's BodySettings row.
  const [savedKind, setSavedKind] = useState<string | null>(null);
  const saveNumbering = useMutation({
    mutationFn: (v: { kind: 'agenda' | 'yaumiyya' | 'ninmun'; format: string; next: string }) =>
      orgSettingsApi.patch({
        body,
        [`${v.kind}NumberFormat`]: v.format.trim() || null,
        ...(v.next.trim() !== '' ? { [`${v.kind}NumberNext`]: Number(v.next) } : {}),
      }),
    onSuccess: (_d, v) => { setSavedKind(v.kind); void qc.invalidateQueries({ queryKey: ['org'] }); },
  });

  const termNumber = org && org.termNumber != null ? String(org.termNumber) : '';

  return (
    <div className="space-y-4">
      <BodyToggle body={body} onChange={(b) => { setBody(b); setSavedKind(null); }} />
      {isLoading || !org ? (
        <p className="text-sm text-muted-foreground">{dv.common.loading}</p>
      ) : (
        <>
          <div className="grid gap-4 xl:grid-cols-3">
            <NumberFormatCard
              key={`agenda-${body}`}
              label={dv.settings.agendaNumberFormat}
              placeholder="AGD/438/{TERM}/{YYYY}/{###}"
              format={org.agendaNumberFormat ?? ''}
              next={String(org.agendaNumberNext)}
              termNumber={termNumber}
              saving={saveNumbering.isPending && saveNumbering.variables?.kind === 'agenda'}
              saved={savedKind === 'agenda'}
              onSave={(format, next) => saveNumbering.mutate({ kind: 'agenda', format, next })}
            />
            <NumberFormatCard
              key={`yaumiyya-${body}`}
              label={dv.settings.yaumiyyaNumberFormat}
              placeholder="YMY/438/{TERM}/{YYYY}/{###}"
              format={org.yaumiyyaNumberFormat ?? ''}
              next={String(org.yaumiyyaNumberNext)}
              termNumber={termNumber}
              saving={saveNumbering.isPending && saveNumbering.variables?.kind === 'yaumiyya'}
              saved={savedKind === 'yaumiyya'}
              onSave={(format, next) => saveNumbering.mutate({ kind: 'yaumiyya', format, next })}
            />
            <NumberFormatCard
              key={`ninmun-${body}`}
              label={dv.settings.ninmunNumberFormat}
              placeholder="NMN/438/{TERM}/{YYYY}/{###}"
              format={org.ninmunNumberFormat ?? ''}
              next={String(org.ninmunNumberNext)}
              termNumber={termNumber}
              saving={saveNumbering.isPending && saveNumbering.variables?.kind === 'ninmun'}
              saved={savedKind === 'ninmun'}
              onSave={(format, next) => saveNumbering.mutate({ kind: 'ninmun', format, next })}
            />
          </div>
          <p className="text-xs text-muted-foreground">{dv.settings.agendaNumberHint}</p>
        </>
      )}
    </div>
  );
}
